import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Layers,
  HardDrive,
  BookOpen,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import type { ConnectorType } from '@/types/api';
import { cn } from '@/lib/utils';

interface AddConnectorDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const CONNECTOR_TYPES: {
  type: ConnectorType;
  label: string;
  description: string;
  icon: typeof Layers;
  color: string;
}[] = [
  { type: 'sharepoint', label: 'SharePoint', description: 'Microsoft SharePoint Online document library', icon: Layers, color: 'text-blue-400' },
  { type: 'google_drive', label: 'Google Drive', description: 'Google Workspace shared drive', icon: HardDrive, color: 'text-green-400' },
  { type: 'confluence', label: 'Confluence', description: 'Atlassian Confluence wiki space', icon: BookOpen, color: 'text-blue-400' },
  { type: 'policystat', label: 'PolicyStat', description: 'PolicyStat policy management', icon: FileText, color: 'text-orange-400' },
];

type CredFields = Record<string, string>;

const INITIAL_CREDS: Record<ConnectorType, CredFields> = {
  sharepoint: { displayName: '', tenantId: '', clientId: '', clientSecret: '', siteUrl: '' },
  google_drive: { displayName: '', serviceAccountJson: '', folderId: '' },
  confluence: { displayName: '', baseUrl: '', username: '', apiToken: '', spaceKey: '' },
  policystat: { displayName: '', subdomain: '' },
};

const FIELD_CONFIG: Record<ConnectorType, { key: string; label: string; type: 'text' | 'password' | 'textarea'; placeholder: string }[]> = {
  sharepoint: [
    { key: 'displayName', label: 'Display Name', type: 'text', placeholder: 'e.g. Corp SharePoint' },
    { key: 'tenantId', label: 'Tenant ID', type: 'text', placeholder: 'Azure AD Tenant ID' },
    { key: 'clientId', label: 'Client ID', type: 'text', placeholder: 'App Registration Client ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'Client secret value' },
    { key: 'siteUrl', label: 'Site URL', type: 'text', placeholder: 'https://org.sharepoint.com/sites/policies' },
  ],
  google_drive: [
    { key: 'displayName', label: 'Display Name', type: 'text', placeholder: 'e.g. Policy Drive' },
    { key: 'serviceAccountJson', label: 'Service Account JSON', type: 'textarea', placeholder: 'Paste service account JSON key...' },
    { key: 'folderId', label: 'Folder ID', type: 'text', placeholder: 'Google Drive folder ID' },
  ],
  confluence: [
    { key: 'displayName', label: 'Display Name', type: 'text', placeholder: 'e.g. Compliance Wiki' },
    { key: 'baseUrl', label: 'Base URL', type: 'text', placeholder: 'https://org.atlassian.net/wiki' },
    { key: 'username', label: 'Username / Email', type: 'text', placeholder: 'admin@company.com' },
    { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'Atlassian API token' },
    { key: 'spaceKey', label: 'Space Key', type: 'text', placeholder: 'e.g. POL' },
  ],
  policystat: [
    { key: 'displayName', label: 'Display Name', type: 'text', placeholder: 'e.g. PolicyStat Bridge' },
    { key: 'subdomain', label: 'Subdomain', type: 'text', placeholder: 'e.g. acme' },
  ],
};

export function AddConnectorDialog({ open, onOpenChange }: AddConnectorDialogProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState<ConnectorType | null>(null);
  const [creds, setCreds] = useState<CredFields>({});
  const [verifyResult, setVerifyResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const createMutation = useMutation({
    mutationFn: () => {
      const { displayName, ...credentials } = creds;
      return api.admin.connectors.create({
        connectorType: selectedType!,
        displayName,
        credentials,
      });
    },
    onSuccess: () => {
      setVerifyResult('success');
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
    onError: (err) => {
      setVerifyResult('error');
      setErrorMessage(err instanceof Error ? err.message : 'Connection verification failed.');
    },
  });

  const reset = () => {
    setStep(1);
    setSelectedType(null);
    setCreds({});
    setVerifyResult(null);
    setErrorMessage('');
  };

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSelectType = (type: ConnectorType) => {
    setSelectedType(type);
    setCreds({ ...INITIAL_CREDS[type] });
  };

  const fields = selectedType ? FIELD_CONFIG[selectedType] : [];
  const allFilled = fields.every((f) => (creds[f.key] ?? '').trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Add Connector — Step {step} of 3
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Select Type */}
        {step === 1 && (
          <div className="grid grid-cols-2 gap-3 py-4">
            {CONNECTOR_TYPES.map((ct) => {
              const Icon = ct.icon;
              return (
                <button
                  key={ct.type}
                  type="button"
                  onClick={() => handleSelectType(ct.type)}
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-lg border p-4 text-center transition-colors',
                    selectedType === ct.type
                      ? 'border-teal bg-teal/5'
                      : 'border-border hover:border-border/80 hover:bg-muted/30',
                  )}
                >
                  <Icon className={cn('h-8 w-8', ct.color)} />
                  <p className="text-sm font-semibold text-foreground">{ct.label}</p>
                  <p className="text-xs text-muted-foreground">{ct.description}</p>
                </button>
              );
            })}
          </div>
        )}

        {/* Step 2: Credentials */}
        {step === 2 && selectedType && (
          <div className="space-y-4 py-4">
            {selectedType === 'policystat' && (
              <p className="text-sm text-muted-foreground">
                PolicyStat uses a manual sync workflow. Add your PolicyStat subdomain
                to enable document mapping notifications.
              </p>
            )}
            {fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`conn-${f.key}`}>{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea
                    id={`conn-${f.key}`}
                    placeholder={f.placeholder}
                    value={creds[f.key] ?? ''}
                    onChange={(e) => setCreds((p) => ({ ...p, [f.key]: e.target.value }))}
                    rows={4}
                  />
                ) : (
                  <Input
                    id={`conn-${f.key}`}
                    type={f.type}
                    placeholder={f.placeholder}
                    value={creds[f.key] ?? ''}
                    onChange={(e) => setCreds((p) => ({ ...p, [f.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Verify */}
        {step === 3 && (
          <div className="flex flex-col items-center gap-4 py-8">
            {createMutation.isPending && (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-teal" />
                <p className="text-sm text-muted-foreground">Testing connection...</p>
              </>
            )}
            {verifyResult === 'success' && (
              <>
                <CheckCircle2 className="h-10 w-10 text-green-400" />
                <p className="text-sm font-medium text-green-400">
                  Connection verified. Connector saved.
                </p>
              </>
            )}
            {verifyResult === 'error' && (
              <>
                <XCircle className="h-10 w-10 text-red-400" />
                <p className="text-sm text-red-400">{errorMessage}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setVerifyResult(null);
                    createMutation.mutate();
                  }}
                >
                  Try again
                </Button>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          {step > 1 && step < 3 && (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
              <ChevronLeft className="mr-1 h-3.5 w-3.5" />
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              disabled={!selectedType}
              onClick={() => setStep(2)}
            >
              Next
              <ChevronRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
          {step === 2 && (
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              disabled={!allFilled}
              onClick={() => {
                setStep(3);
                createMutation.mutate();
              }}
            >
              Test Connection
              <ChevronRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
          {step === 3 && verifyResult === 'success' && (
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              onClick={() => handleClose(false)}
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
